import React from 'react';
import { Button, Tooltip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PhoneDisabledOutlinedIcon from '@mui/icons-material/PhoneDisabledOutlined';
import SpeakerNotesOffOutlinedIcon from '@mui/icons-material/SpeakerNotesOffOutlined';
import NotInterestedOutlinedIcon from '@mui/icons-material/NotInterestedOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import IconAction from '../../../dashboard/components/shared/IconAction';
import { getInitials } from '../../../dashboard/components/shared/PatientItem';
import { chatReading, formatFullDate, plural, whatsappWebUrl } from '../utils';
import styles from '../Recovery.module.css';

const NO_PATTERN_TEXT = 'Sem padrão de horário ainda';
const READING_CHAT_TEXT = 'Lendo a conversa...';

// Cor do chip conforme o que a razão diz: sinal bom da conversa, cautela ou neutro
const REASON_CLASS = {
  no_pattern: 'reasonNeutral',
  chat_wants_time: 'reasonGood',
  chat_interested: 'reasonGood',
  chat_postponed: 'reasonWarn',
  chat_ignored: 'reasonWarn',
  unreliable: 'reasonWarn',
  awaiting_reply: 'reasonWarn',
};

function channelIcon(status) {
  if (status === 'ok') return <WhatsAppIcon style={{ fontSize: 14 }} />;
  if (status === 'declined') return <SpeakerNotesOffOutlinedIcon style={{ fontSize: 14 }} />;
  return <PhoneDisabledOutlinedIcon style={{ fontSize: 14 }} />;
}

function channelClass(status) {
  if (status === 'ok') return styles.channelOk;
  if (status === 'declined') return styles.channelDeclined;
  return styles.channelOff;
}

const CALL_SX = {
  textTransform: 'none',
  fontWeight: 700,
  fontSize: '0.78rem',
  borderRadius: '8px',
  boxShadow: 'none',
  padding: '4px 14px',
  minWidth: 0,
  '&:hover': { boxShadow: 'none' },
};

/**
 * Uma pessoa para chamar: nome (abre o cadastro), último atendimento, razões
 * em chips, o que a IA entendeu da última conversa, estado do canal e as
 * ações. Só canal "ok" libera o Chamar; nos outros o botão fica desabilitado
 * e o tooltip explica o motivo. Enquanto a conversa ainda não foi lida,
 * mostra "Lendo a conversa..." no lugar do sinal. Telefone completo, nota e
 * o atalho do WhatsApp Web estão aí para conferir a conversa e a calibração.
 */
function CandidateRow({ candidate, onCall, onDismiss }) {
  const channel = candidate.channel || { status: 'ok', text: '' };
  const channelOk = channel.status === 'ok';
  const off = Boolean(candidate.called || candidate.dismissed);
  const reasons = candidate.reasons || [];
  const chat = candidate.chat || null;
  const chatSummary = chatReading(chat);

  const meta = [
    candidate.lastVisit ? `Último atendimento ${formatFullDate(candidate.lastVisit)}` : 'Sem atendimento realizado',
    candidate.visits ? plural(candidate.visits, 'atendimento', 'atendimentos') : null,
    candidate.phone || null,
    candidate.score != null ? `nota ${candidate.score}` : null,
  ].filter(Boolean).join(' · ');

  const openRecord = () => window.open(`/patients/${candidate.patientId}`, '_blank', 'noopener');
  const chatUrl = whatsappWebUrl(candidate.phoneDigits);
  const openChat = () => window.open(chatUrl, '_blank', 'noopener');

  const callButton = (
    <span>
      <Button
        size="small"
        variant="contained"
        disableElevation
        disabled={!channelOk}
        onClick={() => onCall(candidate)}
        sx={CALL_SX}
      >
        Chamar
      </Button>
    </span>
  );

  return (
    <div className={`${styles.candidate} ${off ? styles.candidateOff : ''}`}>
      <span className={styles.avatar} aria-hidden="true">{getInitials(candidate.name)}</span>

      <div className={styles.candidateInfo}>
        <div className={styles.candidateNameRow}>
          <button type="button" className={styles.candidateName} onClick={openRecord} title="Abrir cadastro">
            {candidate.name}
          </button>
          {candidate.called && (
            <span className={styles.calledBadge}>
              <CheckIcon style={{ fontSize: 11 }} />
              Chamada
            </span>
          )}
        </div>
        <span className={styles.candidateMeta}>{meta}</span>

        <div className={styles.reasons}>
          {reasons.map((reason) => (
            <span
              key={reason.code}
              className={`${styles.reason} ${REASON_CLASS[reason.code] ? styles[REASON_CLASS[reason.code]] : ''}`}
            >
              {reason.code === 'no_pattern' ? (reason.text || NO_PATTERN_TEXT) : reason.text}
            </span>
          ))}
          {chat?.pending && (
            <span className={`${styles.reason} ${styles.reasonNeutral}`}>{READING_CHAT_TEXT}</span>
          )}
        </div>

        {chatSummary && (
          <span className={styles.chatSummary} title={chatSummary}>{chatSummary}</span>
        )}

        <span className={`${styles.channel} ${channelClass(channel.status)}`}>
          {channelIcon(channel.status)}
          {channel.text || (channelOk ? 'WhatsApp ok' : 'Não dá para chamar por aqui')}
        </span>
      </div>

      <div className={styles.candidateActions}>
        {channelOk ? callButton : <Tooltip title={channel.text || 'Não dá para chamar por aqui'} arrow>{callButton}</Tooltip>}
        <div className={styles.iconRow}>
          <IconAction title="Agora não (não sugerir por 30 dias)" onClick={() => onDismiss(candidate)} disabled={off}>
            <NotInterestedOutlinedIcon style={{ fontSize: 16 }} />
          </IconAction>
          {chatUrl && (
            <IconAction title="Abrir a conversa no WhatsApp Web" onClick={openChat}>
              <WhatsAppIcon style={{ fontSize: 16 }} />
            </IconAction>
          )}
          <IconAction title="Abrir cadastro em nova aba" onClick={openRecord}>
            <OpenInNewOutlinedIcon style={{ fontSize: 16 }} />
          </IconAction>
        </div>
      </div>
    </div>
  );
}

export default CandidateRow;
